import { TextareaContentUtil } from '@axiom/ui';

import type { ChatAnswerSegment } from '../types/chat-answer.types';
import type { ChatEmailDraft } from '../types/chat-email.types';

export type ChatAnswerCopy = { html: string; text: string };

export const ChatAnswerCopyUtil = {
  piecesOf({ container }: { container: HTMLElement | null }) {
    return [
      ...(container?.querySelectorAll<HTMLElement>(':scope > div') ?? []),
    ];
  },

  headerLines({ email }: { email: ChatEmailDraft }) {
    return [
      ['To', email.to],
      ['CC', email.cc],
      ['BCC', email.bcc],
      ['Subject', email.subject],
    ]
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}`);
  },

  draftToHtml({ email, piece }: { email: ChatEmailDraft; piece: HTMLElement }) {
    const headers = ChatAnswerCopyUtil.headerLines({ email }).map(line => {
      const paragraph = document.createElement('p');
      paragraph.textContent = line;

      return paragraph.outerHTML;
    });

    return [
      ...headers,
      TextareaContentUtil.toHtml({ container: piece }) ?? '',
    ].join('');
  },

  draftToText({ email, piece }: { email: ChatEmailDraft; piece: HTMLElement }) {
    return [
      ChatAnswerCopyUtil.headerLines({ email }).join('\n'),
      TextareaContentUtil.toText({ container: piece }) ?? '',
    ]
      .filter(Boolean)
      .join('\n\n');
  },

  /** Fenced as `mermaid`, not `flowchart`: that is the tag anything downstream will know. */
  flowchartToText({ code }: { code: string }) {
    return ['```mermaid', code, '```'].join('\n');
  },

  flowchartToHtml({ code }: { code: string }) {
    const block = document.createElement('pre');

    block.textContent = ChatAnswerCopyUtil.flowchartToText({ code });

    return block.outerHTML;
  },

  /** A pair rather than a format argument, so a new kind cannot answer for one and not the other. */
  copyOf({
    piece,
    segment,
  }: {
    piece: HTMLElement;
    segment: ChatAnswerSegment;
  }): ChatAnswerCopy {
    if (segment.type === 'email') {
      return {
        html: ChatAnswerCopyUtil.draftToHtml({ email: segment.email, piece }),
        text: ChatAnswerCopyUtil.draftToText({ email: segment.email, piece }),
      };
    }

    if (segment.type === 'flowchart') {
      return {
        html: ChatAnswerCopyUtil.flowchartToHtml({ code: segment.code }),
        text: ChatAnswerCopyUtil.flowchartToText({ code: segment.code }),
      };
    }

    return {
      html: TextareaContentUtil.toHtml({ container: piece }) ?? '',
      text: TextareaContentUtil.toText({ container: piece }) ?? '',
    };
  },

  copies({
    container,
    segments,
  }: {
    container: HTMLElement | null;
    segments: ChatAnswerSegment[];
  }): ChatAnswerCopy[] {
    const pieces = ChatAnswerCopyUtil.piecesOf({ container });

    return segments.map((segment, segmentIdx) => {
      const piece = pieces[segmentIdx];

      return piece
        ? ChatAnswerCopyUtil.copyOf({ piece, segment })
        : { html: '', text: '' };
    });
  },

  toHtml({
    container,
    segments,
  }: {
    container: HTMLElement | null;
    segments: ChatAnswerSegment[];
  }) {
    const html = ChatAnswerCopyUtil.copies({ container, segments })
      .map(copy => copy.html)
      .join('');

    return html || null;
  },

  toText({
    container,
    segments,
  }: {
    container: HTMLElement | null;
    segments: ChatAnswerSegment[];
  }) {
    const text = ChatAnswerCopyUtil.copies({ container, segments })
      .map(copy => copy.text)
      .filter(Boolean)
      .join('\n\n');

    return text || null;
  },
};
